import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";

import type { CursorPage, CursorQueryArgs, SortState } from "./types";

export interface UseCursorQueryOptions {
  /** Page size (defaults to DEFAULT_PAGE_LIMIT via toListParams in the fetcher). */
  limit?: number;
  /** Server sort passed through to every page fetch. */
  sort?: SortState;
  /** Gate the query (e.g. wait for a filter to be chosen). Defaults to true. */
  enabled?: boolean;
  /** Testable lower ceiling; production callers cannot exceed the global cap. */
  maxPages?: number;
  /** Testable lower ceiling; production callers cannot exceed the global cap. */
  maxItems?: number;
}

/** Hard safety ceilings for both progressive and eager cursor traversal. */
export const MAX_CURSOR_PAGES = 100;
export const MAX_CURSOR_ITEMS = 20_000;

export class CursorTraversalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CursorTraversalError";
  }
}

export interface CursorPageLike<T> {
  readonly items: readonly T[];
  readonly next_cursor: string | null;
}

export interface CollectCursorPagesOptions {
  signal?: AbortSignal;
  maxPages?: number;
  maxItems?: number;
}

function boundedLimit(requested: number | undefined, hardMaximum: number): number {
  if (requested === undefined) return hardMaximum;
  if (Number.isNaN(requested)) return hardMaximum;
  if (requested === Number.POSITIVE_INFINITY) return hardMaximum;
  if (requested === Number.NEGATIVE_INFINITY) return 1;
  return Math.min(Math.max(1, Math.trunc(requested)), hardMaximum);
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) return;
  if (signal.reason instanceof Error) throw signal.reason;
  throw new DOMException("The cursor request was aborted", "AbortError");
}

function progressiveTraversalError<T>(
  pages: readonly CursorPageLike<T>[],
  pageParams: readonly unknown[],
  maxPages: number,
  maxItems: number,
): CursorTraversalError | null {
  if (pages.length === 0) return null;
  const itemCount = pages.reduce((count, page) => count + page.items.length, 0);
  if (itemCount > maxItems) {
    return new CursorTraversalError(`Cursor traversal exceeded ${maxItems} items`);
  }
  const next = pages[pages.length - 1]?.next_cursor ?? null;
  if (next === null) return null;
  // A non-terminal page at the exact item ceiling has proved that more data
  // exists, but loading it would exceed the bounded traversal. Fail closed
  // now; do not expose or request one page beyond the configured maximum.
  if (itemCount >= maxItems) {
    return new CursorTraversalError(`Cursor traversal exceeded ${maxItems} items`);
  }
  if (pages.length >= maxPages) {
    return new CursorTraversalError(`Cursor traversal exceeded ${maxPages} pages`);
  }
  if (pageParams.some((pageParam) => pageParam === next)) {
    return new CursorTraversalError("Cursor traversal received a repeated cursor");
  }
  return null;
}

/**
 * Resolve a bounded cursor collection. It rejects cycles and oversized result
 * sets instead of spinning forever or silently returning a truncated picker.
 */
export async function collectCursorPages<T>(
  load: (cursor: string | undefined, signal: AbortSignal | undefined) => Promise<CursorPageLike<T>>,
  options: CollectCursorPagesOptions = {},
): Promise<T[]> {
  const maxPages = boundedLimit(options.maxPages, MAX_CURSOR_PAGES);
  const maxItems = boundedLimit(options.maxItems, MAX_CURSOR_ITEMS);
  const items: T[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  let pageCount = 0;

  for (;;) {
    throwIfAborted(options.signal);
    if (pageCount >= maxPages) {
      throw new CursorTraversalError(`Cursor traversal exceeded ${maxPages} pages`);
    }
    const page = await load(cursor, options.signal);
    throwIfAborted(options.signal);
    pageCount += 1;
    if (items.length + page.items.length > maxItems) {
      throw new CursorTraversalError(`Cursor traversal exceeded ${maxItems} items`);
    }
    items.push(...page.items);
    const next = page.next_cursor;
    if (next === null) return items;
    if (items.length >= maxItems) {
      throw new CursorTraversalError(`Cursor traversal exceeded ${maxItems} items`);
    }
    if (seen.has(next)) {
      throw new CursorTraversalError("Cursor traversal received a repeated cursor");
    }
    seen.add(next);
    cursor = next;
  }
}

/**
 * The surface DataTable consumes. useCursorQuery returns this; tests can hand
 * DataTable a hand-rolled literal instead.
 */
export interface CursorQueryResult<T> {
  /** All loaded pages, flattened in order. */
  rows: T[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  /** True during the first page load only (no rows yet). */
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  isFetchingNextPage: boolean;
}

/**
 * Server-driven cursor pagination over useInfiniteQuery.
 *
 * `queryKey` MUST include everything that changes the result set — filters,
 * sort, limit (e.g. `queryKeys.orders.list({ ...filters, sort: sort?.key,
 * dir: sort?.dir })`) — so a sort change starts a fresh page sequence instead
 * of appending to the old one.
 *
 * `fetchPage` receives `{ cursor, limit, sort }`; serialize with
 * `toListParams` and fetch via apiFetch.
 */
export function useCursorQuery<T>(
  queryKey: QueryKey,
  fetchPage: (args: CursorQueryArgs) => Promise<CursorPage<T>>,
  options: UseCursorQueryOptions = {},
): CursorQueryResult<T> {
  const { limit, sort, enabled = true } = options;
  const maxPages = boundedLimit(options.maxPages, MAX_CURSOR_PAGES);
  const maxItems = boundedLimit(options.maxItems, MAX_CURSOR_ITEMS);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => fetchPage({ cursor: pageParam, limit, sort, signal }),
    initialPageParam: null as string | null,
    // next_cursor null => last page; react-query treats null as "no next page".
    getNextPageParam: (lastPage, allPages, _lastPageParam, allPageParams) => {
      const next = lastPage.next_cursor;
      if (next === null) return null;
      return progressiveTraversalError(allPages, allPageParams, maxPages, maxItems) === null
        ? next
        : null;
    },
    enabled,
  });

  const { data } = query;
  const traversalError = useMemo(
    () =>
      data === undefined
        ? null
        : progressiveTraversalError(data.pages, data.pageParams, maxPages, maxItems),
    [data, maxItems, maxPages],
  );
  const rows = useMemo(
    () => (data?.pages.flatMap((page) => page.items) ?? []).slice(0, maxItems),
    [data, maxItems],
  );

  return {
    rows,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isLoading: query.isLoading,
    isError: query.isError || traversalError !== null,
    error: query.error ?? traversalError,
    refetch: query.refetch,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
